import type { SlowQuery } from '@/api/database'

/**
 * Ordenação e filtro da tabela de queries lentas.
 *
 * Funções puras, e isso é decisão: em 18/08/2026 um campo (`janela_segundos`) foi descartado pelo
 * normalizador da API e **nenhum teste pegou**, porque os testes de tela mockam `databaseApi` e
 * pulam a fronteira. Lógica que dá para testar sem render, testa-se sem render.
 */

export type Coluna = 'query' | 'avg_load' | 'calls' | 'avg_latency_ms' | 'total_time_ms'
export type Direcao = 'asc' | 'desc'

/** Ordem em que os cliques no cabeçalho circulam. `null` é a ordem que o servidor mandou. */
export type Ordem = { coluna: Coluna; direcao: Direcao } | null

/**
 * Próximo estado do clique: asc → desc → padrão (a ordem do servidor).
 *
 * O terceiro estado não é "sem ordenação": é a ordem por CARGA que o Performance Insights usou
 * para escolher estas linhas. Voltar a ela é uma informação, não a ausência de uma.
 */
export function proximaOrdem(atual: Ordem, coluna: Coluna): Ordem {
  if (!atual || atual.coluna !== coluna) return { coluna, direcao: 'asc' }
  if (atual.direcao === 'asc') return { coluna, direcao: 'desc' }
  return null
}

/**
 * Chave de texto da query, para ordenação alfabética.
 *
 * Os statements do Performance Insights **começam com espaço e contêm quebras de linha** — medido
 * em 18/08: `"  SELECT \"id\",\n         \"node_id\", …"`. Ordenar o texto cru ordenaria pelo
 * espaço em branco, e a lista pareceria embaralhada.
 */
export function chaveDeTexto(texto: string | null | undefined): string {
  return (texto ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
}

function valorNumerico(q: SlowQuery, coluna: Coluna): number | null {
  switch (coluna) {
    case 'avg_load':
      return q.avg_load
    case 'calls':
      return q.calls
    case 'avg_latency_ms':
      return q.avg_latency_ms
    case 'total_time_ms':
      return q.total_time_ms
    default:
      return null
  }
}

/**
 * Ordena sem nunca tratar ausência como zero.
 *
 * Duas regras que são correção, não preferência:
 *
 * 1. **`null` (o `—` da tela) vai sempre para o FIM**, em `asc` e em `desc`. Ordenar por chamadas
 *    com `null` valendo 0 afirmaria "esta query rodou menos" — e a plataforma não mediu nada. A
 *    regra é **por coluna**: existe linha com `calls` medido e `avg_latency_ms` ausente.
 * 2. **Empate devolve 0**, e o `sort` do JS é estável — então linhas empatadas conservam a ordem
 *    do servidor. Sem isso, as quatro linhas com `total_time_ms = 1000.60` (caso real) trocariam
 *    de lugar a cada render.
 */
export function ordenarQueries(itens: SlowQuery[], ordem: Ordem): SlowQuery[] {
  if (!ordem) return itens
  const { coluna, direcao } = ordem
  const sinal = direcao === 'asc' ? 1 : -1

  return [...itens].sort((a, b) => {
    if (coluna === 'query') {
      return sinal * chaveDeTexto(a.query_text).localeCompare(chaveDeTexto(b.query_text), 'pt-BR')
    }
    const va = valorNumerico(a, coluna)
    const vb = valorNumerico(b, coluna)
    // Ausência não participa da comparação: ela vai para o fim, independente da direção.
    if (va === null && vb === null) return 0
    if (va === null) return 1
    if (vb === null) return -1
    if (va === vb) return 0
    return sinal * (va - vb)
  })
}

/**
 * Filtra por trecho do texto da query, sem distinguir maiúscula.
 *
 * **O filtro age sobre as linhas que o Performance Insights entregou** — no máximo 25, escolhidas
 * por carga. Não existe busca no servidor, então isto nunca é "todas as queries que contêm X"; a
 * tela diz isso em texto, e o contador mostra "N de M".
 *
 * E há uma consequência da tokenização: literais viraram `?`, então procurar por um **valor**
 * (um id, um e-mail) não casa nada. Buscar por comando (`SELECT`, `UPDATE`) ou por tabela funciona.
 */
export function filtrarQueries(itens: SlowQuery[], termo: string): SlowQuery[] {
  const alvo = termo.trim().toLowerCase()
  if (!alvo) return itens
  return itens.filter((q) => (q.query_text ?? '').toLowerCase().includes(alvo))
}

/**
 * Número medido, ou `—` quando o Performance Insights não devolveu a métrica.
 *
 * `0` e `—` são coisas diferentes: `0` é medição, `—` é "não consegui medir". Até 18/08 a ausência
 * era gravada como `0` no backend, e a tabela afirmava "0 chamadas" ao lado de 10 segundos de
 * tempo total.
 */
export function medido(valor: number | null | undefined, casas = 0): string {
  return valor === null || valor === undefined ? '—' : valor.toFixed(casas)
}

/**
 * Rótulo da janela para o cabeçalho: `Chamadas (6h)`, `Chamadas (7d)`.
 *
 * Acima de 48h vira dias, senão o rótulo de sete dias sairia como `(168h)` — número tecnicamente
 * correto que ninguém lê como uma semana.
 */
export function rotuloDaJanela(segundos: number | undefined): string {
  if (!segundos || segundos <= 0) return 'Chamadas'
  const horas = Math.round(segundos / 3600)
  return horas > 48 ? `Chamadas (${Math.round(horas / 24)}d)` : `Chamadas (${horas}h)`
}
